import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, MessageSquareIcon, PhoneIcon, CalendarIcon, DatabaseIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ArrowRightIcon, SmartphoneIcon, GlobeIcon, MailIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function AutomationsPage() {
  const faqs = [{
    question: 'How quickly does the AI respond to leads?',
    answer: 'Our AI systems respond to text messages, lead platform inquiries, and web forms within seconds, 24/7. For missed calls, the AI text-back feature triggers immediately after the call ends.'
  }, {
    question: 'Will it sound like a robot?',
    answer: 'No. We train the AI on your specific business information, services, and tone of voice. It sounds natural, helpful, and professional - most customers never realize they are speaking with AI.'
  }, {
    question: 'Can it actually book appointments?',
    answer: 'Yes. The AI integrates directly with your calendar. It can qualify the lead, answer basic questions, and provide a booking link or directly schedule the appointment based on your real-time availability.'
  }, {
    question: 'What happens if the AI does not know the answer?',
    answer: "If a customer asks a complex question outside the AI's training, it gracefully transitions the conversation. It will notify your team immediately and let the customer know a human will follow up shortly."
  }, {
    question: 'Do I need to change my current phone number?',
    answer: 'No. We can port your existing number into our system, or provide a new tracking number that forwards to your main line while enabling all the AI text-back features.'
  }, {
    question: 'Do you build AI receptionists for landscapers, painters, and other home service businesses?',
    answer: 'Yes. Our AI Lead Conversion system is built specifically for home service operators - landscapers, painters, HVAC, plumbing, roofing, and pest control - who lose jobs when they cannot answer the phone from a jobsite. It answers calls and messages, qualifies the lead, and books the estimate automatically.'
  }];
  return <div className="pt-24 pb-16" data-id="element-518">
      <SEOHead title="AI Lead Conversion | AI Marketing Agency Phoenix | Viridia Analytics" description="Never miss a lead. Our Phoenix AI marketing agency builds AI receptionists and automated follow-up systems for home service contractors to book more jobs." canonical="/ai-lead-conversion" keywords="AI marketing agency phoenix, AI advertising, AI lead conversion, speed to lead, missed call text back, AI receptionist, HVAC, painters, landscapers, contractors, window installers, Phoenix local business leads" faq={faqs} data-id="element-519" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-520">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-521">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-522"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-523">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-524">
            <ScrollReveal data-id="element-525">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-526">
                <ZapIcon className="w-4 h-4" data-id="element-527" />
                <span data-id="element-528">Flagship System</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-529">
                Never Miss a Lead. <br data-id="element-530" />
                <span className="text-viridia" data-id="element-531">Book Jobs Faster.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-4 max-w-2xl" data-id="element-532">
                When a homeowner needs a service, they hire the first contractor
                that responds. Our AI Lead Conversion system answers inbound
                messages, missed calls, and web forms in seconds - qualifying
                prospects and booking estimates 24/7.
              </p>
              <p className="text-lg text-viridia font-medium mb-8 max-w-2xl" data-id="element-533">
                Stop wasting ad spend on leads that go cold while you are on a
                jobsite.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-534">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-535">
                  See Where Your Leads Are Leaking
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-surface border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-536">
                  View Packages
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="hidden lg:block" data-id="element-537">
              <div className="relative" data-id="element-538">
                {/* Abstract visualization of leads flowing into booked jobs */}
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-539">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-540"></div>

                  <div className="flex flex-col gap-6 relative z-10" data-id="element-541">
                    {/* Incoming Leads */}
                    <div className="flex justify-between items-center gap-4" data-id="element-542">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.5
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-543">
                        <div className="w-10 h-10 rounded-full bg-[#FF1A1A]/10 flex items-center justify-center flex-shrink-0" data-id="element-544">
                          <MessageSquareIcon className="w-5 h-5 text-[#FF1A1A]" data-id="element-545" />
                        </div>
                        <div data-id="element-546">
                          <div className="text-xs text-gray-500" data-id="element-547">
                            Lead Request
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-548">
                            "Need a quote for..."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.7
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-549">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0" data-id="element-550">
                          <PhoneIcon className="w-5 h-5 text-blue-500" data-id="element-551" />
                        </div>
                        <div data-id="element-552">
                          <div className="text-xs text-gray-500" data-id="element-553">
                            Missed Call
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-554">
                            From (602) 555-...
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* AI Processing */}
                    <div className="flex justify-center py-2" data-id="element-555">
                      <motion.div animate={{
                      y: [0, 5, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity
                    }} className="w-12 h-12 rounded-full bg-viridia/20 border border-viridia/50 flex items-center justify-center" data-id="element-556">
                        <ZapIcon className="w-6 h-6 text-viridia" data-id="element-557" />
                      </motion.div>
                    </div>

                    {/* Instant Responses */}
                    <div className="space-y-3" data-id="element-558">
                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.2
                    }} className="bg-viridia/5 border border-viridia/20 rounded-xl p-4 flex items-start gap-3 ml-8" data-id="element-559">
                        <div className="w-8 h-8 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 mt-1" data-id="element-560">
                          <ZapIcon className="w-4 h-4 text-white" data-id="element-561" />
                        </div>
                        <div data-id="element-562">
                          <div className="text-xs text-viridia font-medium mb-1" data-id="element-563">
                            Instant AI Response (0.5s)
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-564">
                            "Hi! We can definitely help with that. Are you
                            looking to get this done this week?"
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.8
                    }} className="bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-start gap-3 mr-8" data-id="element-565">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-566">
                          <SmartphoneIcon className="w-4 h-4 text-gray-400" data-id="element-567" />
                        </div>
                        <div data-id="element-568">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-569">
                            Customer
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-570">
                            "Yes, ideally Thursday."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 2.4
                    }} className="bg-viridia/10 border border-viridia/30 rounded-xl p-4 flex items-center justify-between ml-8" data-id="element-571">
                        <div className="flex items-center gap-3" data-id="element-572">
                          <div className="w-10 h-10 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0" data-id="element-573">
                            <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-574" />
                          </div>
                          <div data-id="element-575">
                            <div className="text-sm font-bold text-white" data-id="element-576">
                              Appointment Booked
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-577">
                              Thursday @ 10:00 AM
                            </div>
                          </div>
                        </div>
                        <CheckCircleIcon className="w-6 h-6 text-viridia" data-id="element-578" />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Speed to Lead Comparison */}
      <section className="py-20 bg-viridia-black" data-id="element-579">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-580">
          <ScrollReveal data-id="element-581">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-582">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-583">
                The Financial Cost of a Slow Response
              </h2>
              <p className="text-gray-400" data-id="element-584">
                In local services, the first contractor to reply wins the job
                78% of the time. If you are on a roof, under a sink, or driving,
                you are losing money to competitors who answer instantly.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-585">
            <ScrollReveal data-id="element-586">
              <div className="bg-viridia-surface border border-red-900/30 rounded-2xl p-8 relative overflow-hidden h-full" data-id="element-587">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl" data-id="element-588"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-589">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-590">
                    <ClockIcon className="w-6 h-6 text-red-500" data-id="element-591" />
                  </div>
                  <div data-id="element-592">
                    <h3 className="text-xl font-bold text-white" data-id="element-593">
                      The Old Way
                    </h3>
                    <p className="text-red-400 text-sm" data-id="element-594">
                      Average response: 2-4 hours
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-595">
                  <li className="flex items-start gap-3" data-id="element-596">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-597" />
                    <span className="text-gray-300" data-id="element-598">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-599">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-600" />
                    <span className="text-gray-300" data-id="element-601">
                      Customer immediately calls the next business on the lead
                      platform
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-602">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-603" />
                    <span className="text-gray-300" data-id="element-604">
                      You call back 2 hours later
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-605">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-606" />
                    <span className="text-gray-300" data-id="element-607">
                      "Sorry, we already found someone."
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-red-500/10 rounded-lg border border-red-500/20" data-id="element-608">
                  <p className="text-red-400 font-medium text-center" data-id="element-609">
                    Result: Wasted ad spend & lost revenue
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-610">
              <div className="bg-viridia-surface border border-viridia/30 rounded-2xl p-8 relative overflow-hidden h-full" data-id="element-611">
                <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-3xl" data-id="element-612"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-613">
                  <div className="w-12 h-12 rounded-full bg-viridia/20 flex items-center justify-center" data-id="element-614">
                    <ZapIcon className="w-6 h-6 text-viridia" data-id="element-615" />
                  </div>
                  <div data-id="element-616">
                    <h3 className="text-xl font-bold text-white" data-id="element-617">
                      The Viridia Way
                    </h3>
                    <p className="text-viridia text-sm" data-id="element-618">
                      Average response: &lt; 5 seconds
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-619">
                  <li className="flex items-start gap-3" data-id="element-620">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-621" />
                    <span className="text-gray-300" data-id="element-622">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-623">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-624" />
                    <span className="text-gray-300" data-id="element-625">
                      AI instantly texts: "Hi, sorry we missed you! How can we
                      help?"
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-626">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-627" />
                    <span className="text-gray-300" data-id="element-628">
                      Customer engages with AI instead of calling competitors
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-629">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-630" />
                    <span className="text-gray-300" data-id="element-631">
                      AI qualifies the lead and books the appointment
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-viridia/10 rounded-lg border border-viridia/20" data-id="element-632">
                  <p className="text-viridia font-medium text-center" data-id="element-633">
                    Result: Captured lead & booked job
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps */}
      <section className="py-20 bg-viridia-surface" data-id="element-634">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-635">
          <ScrollReveal data-id="element-636">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-637">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-638">
                How the System Works
              </h2>
              <p className="text-gray-400" data-id="element-639">
                A seamless, automated pipeline that catches every opportunity
                and drives it toward a booked appointment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-640">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-641"></div>

            {[{
            step: '01',
            title: 'Lead Enters',
            desc: 'A customer calls, texts, fills out a form, or messages you through a lead platform.',
            icon: GlobeIcon
          }, {
            step: '02',
            title: 'Instant Response',
            desc: 'Within seconds, our AI receptionist acknowledges the inquiry and starts a conversation.',
            icon: ZapIcon
          }, {
            step: '03',
            title: 'Qualify & Nurture',
            desc: 'The AI asks qualifying questions to ensure they are a good fit for your services.',
            icon: MessageSquareIcon
          }, {
            step: '04',
            title: 'Book & Track',
            desc: 'The lead is booked on your calendar and tracked in your CRM pipeline.',
            icon: DatabaseIcon
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-642">
                <div className="relative z-10 bg-viridia-black border border-gray-800 rounded-2xl p-6 h-full flex flex-col" data-id="element-643">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-6 mx-auto" data-id="element-644">
                    <item.icon className="w-5 h-5 text-viridia" data-id="element-645" />
                  </div>
                  <div className="text-center flex-grow" data-id="element-646">
                    <div className="text-xs font-bold text-viridia mb-2" data-id="element-647">
                      STEP {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3" data-id="element-648">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-649">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Scenario Cards */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-650">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-651">
          <ScrollReveal data-id="element-652">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-653">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-654">
                Built for Real Scenarios
              </h2>
              <p className="text-gray-400" data-id="element-655">
                How our AI handles the situations that usually cost you money.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-656">
            <ScrollReveal data-id="element-657">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-658">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-659">
                  Scenario 1: The After-Hours Lead
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-660">
                  A Yelp or Google lead comes in after hours, gets an immediate
                  response, is qualified, routed, and followed up with before
                  your competitor calls them back.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-661">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-662">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-663">
                    "Hi there! We offer 24/7 emergency AC repair. What seems to
                    be the issue with your unit?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-664">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-665" /> Lead captured while
                    competitors sleep
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.1} data-id="element-666">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-667">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-668">
                  Scenario 2: The "On The Job" Missed Call
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-669">
                  You are under a sink fixing a leak. Your phone rings. You
                  cannot answer it.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-670">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-671">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-672">
                    "Hi, this is [Your Business]. Sorry we missed your call! Are
                    you looking to schedule a plumbing service?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-673">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-674" /> Stops them from
                    calling the next plumber
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-675">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 h-full" data-id="element-676">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-677">
                  Scenario 3: The Tire Kicker
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-678">
                  Someone fills out a web form asking for a quote, but they
                  aren't ready to buy yet.
                </p>
                <div className="space-y-3 bg-viridia-black rounded-xl p-4 border border-gray-800" data-id="element-679">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-680">
                    Viridia AI Nurture Sequence
                  </div>
                  <div className="text-sm text-white" data-id="element-681">
                    Automated follow-ups on Day 2, Day 5, and Day 14 with
                    helpful tips and a soft CTA.
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-682">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-683" /> Stays top-of-mind
                    without manual effort
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-684">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-685">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-686">
            <ScrollReveal data-id="element-687">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-688">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-689">
                Our AI Lead Conversion system is designed specifically for home
                service operators who are tired of losing jobs to faster
                competitors.
              </p>
              <ul className="space-y-4" data-id="element-690">
                <li className="flex items-start gap-3" data-id="element-691">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-692" />
                  <div data-id="element-693">
                    <h4 className="font-bold text-white" data-id="element-694">
                      Contractors & Home Services
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-695">
                      HVAC, plumbing, landscaping, roofing, painting, window
                      installers, and pest control.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-696">
                  <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-697" />
                  <div data-id="element-698">
                    <h4 className="font-bold text-white" data-id="element-699">
                      Businesses Running Ads
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-700">
                      If you are paying for clicks, you need a system to ensure
                      those clicks turn into conversations instantly.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-701">
                  <PhoneIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-702" />
                  <div data-id="element-703">
                    <h4 className="font-bold text-white" data-id="element-704">Busy Operators</h4>
                    <p className="text-sm text-gray-400" data-id="element-705">
                      Owners who are often on the jobsite and cannot always
                      answer the phone immediately.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-706">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-707">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-708">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-709">
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-710">
                    <div className="font-bold text-white mb-1" data-id="element-711">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-712">
                      Organize your pipeline and track every lead our AI
                      captures.
                    </div>
                  </Link>
                  <Link to="/ad-campaigns" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-713">
                    <div className="font-bold text-white mb-1" data-id="element-714">
                      Ad Campaigns
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-715">
                      Drive high-intent traffic into your new automated
                      conversion system.
                    </div>
                  </Link>
                  <Link to="/blog/ai-receptionist-vs-human-receptionist-cost" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-716">
                    <div className="font-bold text-white mb-1" data-id="element-717">
                      AI vs. Human Receptionist: Cost Comparison
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-718">
                      See the real cost breakdown before you decide how to
                      staff your front line.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-719">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-720">
          <ScrollReveal data-id="element-721">
            <div className="text-center mb-12" data-id="element-722">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-723">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <div className="space-y-4" data-id="element-724">
            {faqs.map((faq, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-725">
                <details className="group bg-viridia-surface border border-gray-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-id="element-726">
                  <summary className="flex items-center justify-between p-6 cursor-pointer text-white font-medium" data-id="element-727">
                    {faq.question}
                    <ChevronDownIcon className="w-5 h-5 text-gray-400 transition-transform group-open:-rotate-180" data-id="element-728" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-400 text-sm leading-relaxed" data-id="element-729">
                    {faq.answer}
                  </div>
                </details>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-730">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-731">
          <ScrollReveal data-id="element-732">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-733">
              See where your leads are leaking
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-734">
              Book a strategy call to see how our AI can start converting your
              leads into booked jobs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-735">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-736">
                Request a Lead Flow Review
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-black border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-737">
                View Packages
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}